<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import CreatePromoCodeApplication from '~/components/modal-windows/CreatePromoCodeApplication.vue'
import PartnerButton from '~/components/partner/PartnerButton.vue'
import PartnerSection from '~/components/partner/PartnerSection.vue'
import { useSharing } from '~/composables/sharing'
import Page from '~/enums/page'
import QueryAction from '~/enums/query-action'
import { useAuthStore } from '~/store/auth'
import { useUserStore } from '~/store/user'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'

const { $apiHelper, $toast } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { parsePromoCodeFromQuery } = useSharing()
const userStore = useUserStore()

const advantages = [
  'Будете иметь под\xA0рукой одну платформу со\xA0всеми необходимыми суплементами, за\xA0качество которых не\xA0нужно беспокоиться',
  'Повысите лояльность клиентов за\xA0счёт сотрудничества с\xA0ведущим магазином добавок',
  'Станете участником акций, конкурсов и\xA0розыгрышей только для партнёров. Получите скидки на\xA0мероприятия интегративного мира',
  'Будете проходить обучения по\xA0продуктам, представленным на\xA0iTAB',
  'Сможете задавать любые вопросы внутри экспертного сообщества iTAB и\xA0оперативно получать ответы от\xA0ведущих специалистов',
  'Получите доступ к\xA0тематическим подборкам под разный запрос и\xA0готовым решениям для экспертного контента',
  'Сможете составлять полноценные рекомендации для своих клиентов прямо на\xA0iTAB',
  'Присоединитесь к\xA0закрытому телеграм-каналу для партнёров, где публикуется информация о\xA0новинках, специальных предложениях и\xA0всех обновлениях на\xA0сайте',
  'И\xA0многое другое!'
]
const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: Page.Main }]
const messages = {
  failedToGetUser: 'Не удалось получить пользователя.'
}
const partners = [
  {
    imagePath: '/images/pages/partner/partners/1.jpg',
    name: 'Нутрициолог или health-coach'
  },
  {
    imagePath: '/images/pages/partner/partners/2.jpg',
    name: 'Врач с\xA0превентивным или интегративным подходом'
  },
  {
    imagePath: '/images/pages/partner/partners/3.jpg',
    name: 'Фитнес-тренер'
  },
  {
    imagePath: '/images/pages/partner/partners/4.jpg',
    name: 'Косметолог'
  },
  {
    imagePath: '/images/pages/partner/partners/5.jpg',
    name: 'ЗОЖ-блогер с\xA0большой и\xA0активной аудиторией'
  }
]
const title = 'Программа лояльности для специалистов'

const userData = ref<any>(null)

useHead({
  title: `${title} — iTAB`,
  meta: [{
    name: 'description',
    content: 'Программа лояльности для специалистов. Станьте партнёром iTAB, крупнейшего маркетплейса добавок и товаров для здоровья, и зарабатывайте вместе с нами.'
  }]
})

onMounted(async (): Promise<void> => {
  await parsePromoCodeFromQuery()

  if (authStore.isUser) {
    await loadUserData()
  }

  await executeAction()
})

async function executeAction (): Promise<void> {
  const query = { ...route.query }
  const { action } = query

  if (action === QueryAction.OpenCreatePromoCodeApplicationModalWindow) {
    delete query.action

    await router.replace({ query })

    await openCreatePromoCodeApplicationModalWindow()
  }
}

async function loadUserData (): Promise<void> {
  try {
    const { data } = await $apiHelper.users.getUser(authStore.userId as string)

    userStore.updateFromUserData(data)

    userData.value = data
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToGetUser)
  }
}

async function openCreatePromoCodeApplicationModalWindow (): Promise<void> {
  await useModal({ component: CreatePromoCodeApplication, attrs: { userData: userData.value } }).open()
}
</script>

<template>
  <div class="partner">
    <section>
      <div class="container">
        <Breadcrumbs :current="title" :items="breadcrumbsItems" />

        <h1 class="visually-hidden">
          {{ title }}
        </h1>
      </div>
    </section>

    <PartnerSection :banner-id="1" @on-button-click="openCreatePromoCodeApplicationModalWindow" />

    <section class="partner__invite-block mt-10">
      <div class="container">
        <div class="partner__invite">
          <div class="partner__invite-presentation-block md:flex">
            <div>
              <div class="partner__invite-presentation-block-title text-white">
                <span class="partner__low-font">Миссия iTAB для клиентов&nbsp;— стать верным помощником в&nbsp;достижениях,</span> а&nbsp;для специалистов&nbsp;— стать надёжным партнёром и&nbsp;простым способом дополнительного заработка.
              </div>
            </div>

            <div class="partner__invite-presentation-block-card bg-white">
              <p>
                Прежде, чем появиться на&nbsp;нашем сайте, каждый товар проходит строгий контроль нашей медицинской комиссии.
              </p>

              <p>
                Мы&nbsp;очень дорожим доверием наших клиентов и&nbsp;партнёров, поэтому подходим к&nbsp;своей работе с&nbsp;большой ответственностью.
              </p>
            </div>
          </div>

          <div class="h1 text-white">
            Если вы:
          </div>

          <div class="-mb-6 flex flex-wrap justify-between">
            <div v-for="{ name, imagePath } in partners" :key="name" class="partner__invite-item bg-white text-center">
              <img v-lazy="imagePath" alt="" class="partner__invite-item-img" loading="lazy">

              <p>
                {{ name }}
              </p>
            </div>

            <div class="partner__invite-item partner__invite-item--last bg-white text-center">
              <div class="inner">
                <p class="partner__plus flex items-center justify-center text-white">
                  <SvgIcon name="outlined/add" />
                </p>

                <p class="text-white">
                  Мы&nbsp;приглашаем вас стать нашим партнёром!
                </p>

                <PartnerButton :color-type="2" :text-type="1" @click="openCreatePromoCodeApplicationModalWindow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="partner__promo-code-block bg-white">
      <div class="container">
        <div>
          <div class="partner__promo-code-block-container flex-wrap">
            <div class="partner__promo-code-block-container-left-side">
              <div class="h1 partner__promo-code-block-title">
                Став амбассадором iTAB, вы&nbsp;получите персональный промокод
              </div>

              <p>По&nbsp;нему ваши клиенты будут получать приятную скидку&nbsp;5%, а&nbsp;вы&nbsp;— кешбэк&nbsp;5% с&nbsp;каждого заказа!</p>

              <p>Накопленные баллы вы&nbsp;сможете выводить в&nbsp;виде реальных денежных средств или тратить на&nbsp;продукты из&nbsp;нашего каталога.</p>

              <PartnerButton :color-type="2" :text-type="2" @click="openCreatePromoCodeApplicationModalWindow" />
            </div>

            <div class="partner__promo-code-block-container-right-side">
              <img
                v-lazy="'/images/pages/partner/promo-code/woman.jpg'"
                alt=""
                class="partner__invite-item-img"
                loading="lazy"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="partner__slider-container">
      <div class="container">
        <div class="h1">
          Кроме дополнительного заработка, вы:
        </div>

        <div class="partner__slider-block flex-wrap">
          <div
            v-for="x in advantages"
            :key="x"
            class="partner__slider-item flex flex-col items-center bg-white text-center"
          >
            <img alt="" class="mb-6" loading="lazy" src="/images/pages/partner/advantages/heart.png">
            <p>
              {{ x }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <PartnerSection :banner-id="2" class="mt-10" @on-button-click="openCreatePromoCodeApplicationModalWindow" />

    <section class="partner__agreement mt-10">
      <div class="container">
        <div class="partner__agreement-inner text-center text-white">
          Полные условия реферальной программы для специалистов вы&nbsp;можете прочесть <NuxtLink class="text-white underline" :to="Page.Account">
            <span>в&nbsp;вашем «Личном кабинете»</span>
          </NuxtLink>.
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.partner {
  &__invite-block {
    background: linear-gradient(to bottom, #01ae75, #49bb70);
    padding: 40px 0;
  }

  &__invite-presentation-block {
    &-title {
      font-size: 24px;
      font-weight: 700;
      line-height: 30px;
      width: 90%;

      @media (max-width: 768px) {
        font-size: 25px;
        line-height: 30px;
      }

      .partner__low-font {
        font-weight: 100;
      }
    }
  }

  &__black-color {
    color: #131313;
  }

  &__invite-presentation-block-card {
    border-radius: 16px;
    padding: 24px;

    p {
      color: #131313;
      font-size: 18px;
      line-height: 24px;

      @media (max-width: 768px) {
        font-size: 16px;
        line-height: 19px;
      }
    }
  }

  &__invite-item {
    border-radius: 16px;
    flex-basis: calc(33.333333% - 16px);
    margin-bottom: 24px;
    padding: 24px;

    @media (max-width: 768px) {
      flex-basis: calc(50% - 4px);
      margin-bottom: 16px;
      padding: 12px;
    }

    &-img {
      max-width: 100%;

      img {
        max-width: 100%;
      }
    }

    &--last {
      background-color: transparent;
      padding: 0;

      .inner {
        border: 2px solid white;
        border-radius: 16px;
        box-sizing: border-box;
        height: 100%;
        padding: 16px;

        @media (max-width: 768px) {
          padding: 12px;
        }
      }

      .partner__plus {
        border-radius: 50%;
        border: 2px solid white;
        font-size: 48px;
        height: 150px;
        margin: 32px auto;
        width: 150px;

        @media (max-width: 768px) {
          font-size: 32px;
          height: 56px;
          margin: 0 auto;
          width: 56px;
        }
      }
    }

    p {
      font-size: 22px;
      line-height: 32px;
      margin-bottom: 16px;
      margin-top: 16px;
      text-align: center;

      @media (max-width: 768px) {
        font-size: 16px;
        line-height: 18px;
        margin-top: 12px;
      }
    }
  }

  &__promo-code-block {
    padding: 40px 0;

    &-container {
      display: flex;

      &-left-side {
        flex-basis: 70%;
      }

      &-right-side {
        flex-basis: 30%;
      }
    }

    p {
      font-size: 20px;
      line-height: 24px;
      width: 70%;

      @media (max-width: 772px) {
        font-size: 16px;
        line-height: 20px;
        width: 100%;
      }
    }

    &-title {
      width: 90%;

      @media (max-width: 772px) {
        width: auto;
      }
    }
  }

  &__slider-container {
    background: #DCF9ED;
    padding: 40px 0;
  }

  &__slider-block {
    display: flex;
    margin: -12px;
  }

  &__slider-item {
    border-radius: 12px;
    flex-basis: calc(33.333333% - 24px);
    margin: 12px;
    padding: 24px;

    img {
      height: 71px;
      width: 71px;
    }
  }

  &__agreement {
    background: linear-gradient(to bottom, #01ae75, #49bb70);
    padding: 40px 0;

    &-inner {
      font-size: 18px;
      line-height: 24px;
      margin: 0 auto;
      width: 50%;

      @media (max-width: 786px) {
        font-size: 16px;
        line-height: 22px;
        width: 100%;
      }
    }
  }

  .partner__promo-code-block-container-right-side {
    .partner__invite-item-img {
      height: 360px;
    }
  }

  @media (max-width: 992px) {
    .partner__slider-item {
      flex-basis: calc(50% - 20px);
    }
  }

  @media (max-width: 768px) {
    .partner__invite-presentation-block-card {
      margin: 20px 0;
    }

    .partner__promo-code-block-container {
      flex-direction: column-reverse;
    }

    .partner__promo-code-block-container-left-side,
    .partner__promo-code-block-container-right-side {
      flex-basis: 100%;

      .partner__invite-item-img {
        display: flex;
        margin: 0 auto;
      }
    }

    .partner__promo-code-block-container-right-side {
      margin-bottom: 24px;
    }
  }

  @media (max-width: 576px) {
    .partner__slider-item {
      flex-basis: calc(100% - 20px);
    }
  }
}
</style>
